import { flow, types } from 'mobx-state-tree'

import Auth from '../../../../helpers/auth'
import type { MatchValidationResponse } from '../../../../types/props'
import { RequestHandler } from '../../../api/RequestHandler'
import MatchesSearchMap, { createMatchesSearchMap } from './MatchesSearchMap'

const MatchesSearchStore = types
  .model('MatchesSearchStore', {
    results: types.map(MatchesSearchMap),
    state: types.enumeration('State', ['pending', 'done', 'error']),
    pendingFilters: types.array(types.string),
    failedFilters: types.map(types.string),
  })
  .views(self => ({
    getSearchState(filter: string): 'idle' | 'pending' | 'done' | 'error' {
      if (self.pendingFilters.includes(filter)) return 'pending'
      if (self.failedFilters.has(filter)) return 'error'
      return self.results.has(filter) ? 'done' : 'idle'
    },
  }))
  .actions(self => {
    const getMatches = async (startDate: string, endDate: string) => {
      const filter = `${startDate}_${endDate}`
      const data = self.results.get(filter)
      if (data) {
        return data
      }
      if (self.pendingFilters.includes(filter) || self.failedFilters.has(filter)) {
        return null
      }
      return fetchMatches(startDate, endDate)
    }
    const fetchMatches = flow(function* fetchMatches(startDate: string, endDate: string) {
      const filter = `${startDate}_${endDate}`
      if (self.pendingFilters.includes(filter)) return
      self.pendingFilters.push(filter)
      self.failedFilters.delete(filter)
      self.state = 'pending'

      // construct props
      const tokens = Auth.getTokens()
      const method = 'GET'
      const url = `${import.meta.env.VITE_API_URL}matches`
      const params = {
        startdate: startDate,
        enddate: endDate,
      }

      // make request
      try {
        const response = yield RequestHandler({
          method,
          url,
          params,
          headers: { Authorization: `Bearer ${tokens?.accessToken}` },
        })
        if (!response || !response.ok) throw new Error('Match-list request failed')
        const data: unknown = yield response.json()
        self.results.put(createMatchesSearchMap(filter, data))
        self.state = 'done'
        return self.results.get(filter)?.data
      } catch {
        self.failedFilters.set(filter, 'Unable to load matches. Please retry or check your sign-in session.')
        self.state = 'error'
      } finally {
        self.pendingFilters.remove(filter)
      }
    })
    const validateSdsMatch = flow(function* validateSdsMatch(match) {
      self.state = 'pending'
      const responseDefault: MatchValidationResponse = {
        match: [],
        competition: [],
        players: [],
        officials: [],
        team: [],
        venue: [],
      }

      // construct props
      const tokens = Auth.getTokens()
      const method = 'POST'
      const url = `${import.meta.env.VITE_API_URL}sds/validate`
      const params = {}
      const body = JSON.stringify(match)

      // make request
      try {
        const response = yield RequestHandler({
          method,
          url,
          params,
          body,
          headers: { Authorization: `Bearer ${tokens?.accessToken}` },
        })
        self.state = 'done'
        if (response && response.ok) {
          return yield response.json()
        }
        return responseDefault
      } catch (error) {
        console.warn('Error checking SDS Mappings', error) // eslint-disable-line no-console
        self.state = 'error'
        return responseDefault
      }
    })
    return {
      getMatches,
      fetchMatches,
      validateSdsMatch,
    }
  })

export default MatchesSearchStore
